import { useEffect, useRef, useState, useCallback } from "react";
import { X, MapPin } from "lucide-react";
import { getResponsesByStream } from "../../lib/api";
import type { StadtteilAggregate } from "../../lib/api";
import {
  STADTTEIL_SLUGS,
  STADTTEIL_DISPLAY_NAMES,
} from "../../lib/stadtteilSlugs";
import mapSvg from "../../assets/hannover-stadtbezirke.svg?raw";
import { ShareButton } from "./ShareButton";

// ── Constants ─────────────────────────────────────────────────────────────────

const REFRESH_INTERVAL_MS = 30_000;

/** Theme colour palette — same rotation as ThemeVelocity */
const THEME_COLORS = ["#DB1270", "#C6F430", "#B4B4BA", "#111113", "#707078"] as const;

/** Fill for districts with zero responses */
const FOG_FILL = "#E2E2E5";

/** Label color for inactive tiles — rc-charcoal, passes ~8:1 against rc-fog */
const INACTIVE_LABEL = "#3A3A40";

/** Relative luminance per WCAG. */
function luminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lin = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Pick rc-paper or rc-ink so the label always passes WCAG large-text on the fill. */
function labelOnFill(fill: string): string {
  return luminance(fill) < 0.45 ? "#FFFFFF" : "#111113";
}

const DISPLAY_NAMES: Record<string, string> = STADTTEIL_DISPLAY_NAMES;
const ALL_SLUGS = STADTTEIL_SLUGS;

// ── Types ─────────────────────────────────────────────────────────────────────

interface TooltipState {
  slug: string;
  x: number;
  y: number;
}

interface PanelState {
  slug: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDisplayName(slug: string): string {
  return DISPLAY_NAMES[slug] ?? slug;
}

/** Assign a fill colour based on the dominant theme index (top_clusters[0]). */
function getDistrictFill(
  agg: StadtteilAggregate | undefined,
  themeColorIndex: number,
): string {
  if (!agg || agg.response_count === 0) return FOG_FILL;
  return THEME_COLORS[themeColorIndex % THEME_COLORS.length];
}

/** Build a map from geo_tag → colour index (stable across re-renders via slug list). */
function buildColorMap(data: StadtteilAggregate[]): Map<string, number> {
  // Assign colours in order of response volume (most responses → first colour)
  const sorted = [...data]
    .filter((d) => d.response_count > 0)
    .sort((a, b) => b.response_count - a.response_count);
  const map = new Map<string, number>();
  sorted.forEach((d, i) => map.set(d.geo_tag, i));
  return map;
}

// ── Tooltip ───────────────────────────────────────────────────────────────────

interface TooltipProps {
  tooltip: TooltipState;
  aggMap: Map<string, StadtteilAggregate>;
  colorMap: Map<string, number>;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

function HeatmapTooltip({ tooltip, aggMap, colorMap, containerRef }: TooltipProps) {
  const agg = aggMap.get(tooltip.slug);
  const colorIdx = colorMap.get(tooltip.slug) ?? 0;
  const displayName = getDisplayName(tooltip.slug);

  // Position relative to container
  const containerRect = containerRef.current?.getBoundingClientRect();
  const left = containerRect ? tooltip.x - containerRect.left : tooltip.x;
  const top = containerRect ? tooltip.y - containerRect.top : tooltip.y;

  return (
    <div
      className="absolute z-20 pointer-events-none"
      style={{
        left: Math.max(8, left + 12),
        top: Math.max(8, top - 12),
        maxWidth: 220,
      }}
      role="tooltip"
    >
      <div className="bg-rc-ink text-rc-paper rounded-xl shadow-cardLg px-4 py-3 font-sans text-[12px]">
        <p className="font-semibold text-[13px] mb-2 leading-tight">{displayName}</p>
        {agg && agg.response_count > 0 ? (
          <>
            <p className="text-rc-stone mb-2 tabular-nums">
              {agg.response_count}{" "}
              {agg.response_count === 1 ? "Beitrag" : "Beiträge"}
            </p>
            {agg.top_clusters.length > 0 && (
              <div className="flex flex-col gap-1">
                {agg.top_clusters.slice(0, 3).map((c, i) => (
                  <div key={c.label} className="flex items-center gap-1.5">
                    <span
                      className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                      style={{
                        background:
                          THEME_COLORS[
                            (colorIdx + i) % THEME_COLORS.length
                          ],
                      }}
                    />
                    <span className="text-rc-stone truncate" style={{ maxWidth: 120 }}>
                      {c.label}
                    </span>
                    <span className="ml-auto font-semibold tabular-nums text-rc-paper">
                      {c.size}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-rc-stone">Keine Beiträge</p>
        )}
      </div>
    </div>
  );
}

// ── Side Panel ────────────────────────────────────────────────────────────────

interface SidePanelProps {
  panel: PanelState;
  aggMap: Map<string, StadtteilAggregate>;
  colorMap: Map<string, number>;
  onClose: () => void;
}

function SidePanel({ panel, aggMap, colorMap, onClose }: SidePanelProps) {
  const agg = aggMap.get(panel.slug);
  const colorIdx = colorMap.get(panel.slug) ?? 0;
  const displayName = getDisplayName(panel.slug);

  // Close on Escape key
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      className="flex flex-col h-full bg-rc-paper border-l border-rc-fog overflow-y-auto"
      aria-label={`Details für ${displayName}`}
      role="complementary"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-rc-fog">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-rc-berry mb-1">
            Stadtbezirk
          </p>
          <h3 className="text-[17px] font-bold text-rc-black leading-tight">
            {displayName}
          </h3>
          {agg && (
            <p className="text-[13px] text-rc-slate mt-1 tabular-nums">
              {agg.response_count}{" "}
              {agg.response_count === 1 ? "Beitrag" : "Beiträge"}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 w-8 h-8 rounded-full bg-rc-fog hover:bg-rc-stone/30 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rc-berry focus-visible:ring-offset-2"
          aria-label="Panel schließen"
        >
          <X className="w-4 h-4 text-rc-slate" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-5">
        {!agg || agg.response_count === 0 ? (
          <p className="text-[13px] text-rc-slate leading-[1.6]">
            Noch keine Beiträge aus diesem Stadtbezirk.
          </p>
        ) : (
          <>
            {/* Top themes */}
            {agg.top_clusters.length > 0 && (
              <div className="mb-6">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-rc-slate mb-3">
                  Top-Themen
                </p>
                <div className="flex flex-col gap-2">
                  {agg.top_clusters.map((c, i) => (
                    <div
                      key={c.label}
                      className="flex items-center gap-3 py-2 px-3 rounded-xl bg-rc-cream border border-rc-fog"
                    >
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{
                          background:
                            THEME_COLORS[
                              (colorIdx + i) % THEME_COLORS.length
                            ],
                        }}
                      />
                      <span className="text-[13px] text-rc-black font-medium flex-1 min-w-0 truncate">
                        {c.label}
                      </span>
                      <span className="text-[13px] font-semibold text-rc-slate tabular-nums flex-shrink-0">
                        {c.size}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Example responses */}
            {agg.example_responses.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-rc-slate mb-3">
                  Beispiel-Beiträge
                </p>
                <div className="flex flex-col gap-2">
                  {agg.example_responses.map((r, i) => (
                    <div
                      key={i}
                      className="bg-rc-cream border border-rc-fog rounded-xl px-4 py-3"
                    >
                      <p className="text-[13px] text-rc-charcoal leading-[1.55] italic">
                        „{r.text}{r.text.length >= 80 ? "…" : ""}"
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Social card content ───────────────────────────────────────────────────────

interface StadtteilSocialContentProps {
  data: StadtteilAggregate[];
  colorMap: Map<string, number>;
  eventTitle: string;
}

function StadtteilSocialContent({ data, colorMap, eventTitle }: StadtteilSocialContentProps) {
  const top5 = data
    .filter((d) => d.response_count > 0)
    .sort((a, b) => b.response_count - a.response_count)
    .slice(0, 5);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#DB1270",
          marginBottom: 4,
        }}
      >
        Stadtteile · {eventTitle}
      </div>
      {top5.map((d) => {
        const idx = colorMap.get(d.geo_tag) ?? 0;
        const fill = THEME_COLORS[idx % THEME_COLORS.length];
        const displayName =
          DISPLAY_NAMES[d.geo_tag] ?? d.geo_tag;
        return (
          <div
            key={d.geo_tag}
            style={{ display: "flex", alignItems: "center", gap: 12 }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: fill,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                flex: 1,
                fontSize: 18,
                fontWeight: 500,
                color: "#000000",
                lineHeight: 1.4,
              }}
            >
              {displayName}
            </span>
            <span
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#000000",
              }}
            >
              {d.response_count}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

interface StadtteilHeatmapProps {
  eventId: string;
  eventTitle?: string;
}

export function StadtteilHeatmap({ eventId, eventTitle = "" }: StadtteilHeatmapProps) {
  const [data, setData] = useState<StadtteilAggregate[]>([]);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [panel, setPanel] = useState<PanelState | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  // Track whether the stagger animation has played already
  const staggerPlayedRef = useRef(false);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const result = await getResponsesByStream(eventId);
      if (mountedRef.current) {
        setData(result);
      }
    } catch {
      // Keep last good data on transient errors
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [eventId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    intervalRef.current = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fetchData]);

  // ── Build lookup maps ──────────────────────────────────────────────────────
  const aggMap = new Map<string, StadtteilAggregate>(
    data.map((d) => [d.geo_tag, d]),
  );
  const colorMap = buildColorMap(data);

  // ── Attach SVG interactivity after mount / data / selection change ────────
  useEffect(() => {
    const container = mapRef.current;
    if (!container) return;

    const handlers: Array<{ el: Element; type: string; fn: EventListener }> = [];

    function addHandler(el: Element, type: string, fn: EventListener) {
      el.addEventListener(type, fn);
      handlers.push({ el, type, fn });
    }

    const isFirstRender = !staggerPlayedRef.current;
    if (isFirstRender) {
      staggerPlayedRef.current = true;
    }

    const svgNs = "http://www.w3.org/2000/svg";

    function upsertText(parent: SVGElement, tag: string): SVGTextElement {
      const existing = parent.parentNode?.querySelector<SVGTextElement>(
        `text[data-tag="${tag}"][data-for="${parent.id}"]`,
      );
      if (existing) return existing;
      const t = document.createElementNS(svgNs, "text");
      t.setAttribute("data-tag", tag);
      t.setAttribute("data-for", parent.id);
      t.style.pointerEvents = "none";
      t.setAttribute("text-anchor", "middle");
      t.setAttribute("font-family", "IBM Plex Sans, Helvetica, sans-serif");
      parent.parentNode?.insertBefore(t, parent.nextSibling);
      return t;
    }

    ALL_SLUGS.forEach((slug, slugIdx) => {
      const el = container.querySelector(`#${slug}`);
      if (!el) return;

      const agg = aggMap.get(slug);
      const colorIdx = colorMap.get(slug) ?? -1;
      const fill = getDistrictFill(agg, colorIdx);
      const displayName = getDisplayName(slug);
      const count = agg?.response_count ?? 0;
      const isActive = count > 0;
      const isSelected = panel?.slug === slug;
      const topThemeLabels = agg?.top_clusters.slice(0, 3).map((c) => c.label).join(", ") ?? "";

      const svgEl = el as SVGElement;
      const rectX = parseFloat(svgEl.getAttribute("x") ?? "0");
      const rectY = parseFloat(svgEl.getAttribute("y") ?? "0");
      const rectW = parseFloat(svgEl.getAttribute("width") ?? "0");
      const rectH = parseFloat(svgEl.getAttribute("height") ?? "0");
      const cx = rectX + rectW / 2;
      const cy = rectY + rectH / 2;

      // Refresh accessibility metadata
      svgEl.querySelector("title[data-a11y]")?.remove();
      svgEl.querySelector("desc[data-a11y]")?.remove();
      const titleEl = document.createElementNS(svgNs, "title");
      titleEl.setAttribute("data-a11y", "1");
      titleEl.textContent = displayName;
      svgEl.insertBefore(titleEl, svgEl.firstChild);
      const descEl = document.createElementNS(svgNs, "desc");
      descEl.setAttribute("data-a11y", "1");
      descEl.textContent = isActive
        ? `${count} ${count === 1 ? "Beitrag" : "Beiträge"}${topThemeLabels ? `. Top-Themen: ${topThemeLabels}` : ""}`
        : "Keine Beiträge";
      svgEl.insertBefore(descEl, svgEl.children[1] ?? null);

      svgEl.setAttribute("tabindex", "0");
      svgEl.setAttribute("role", "button");
      svgEl.setAttribute(
        "aria-label",
        `${displayName}, ${count} ${count === 1 ? "Beitrag" : "Beiträge"}`,
      );
      svgEl.style.cursor = "pointer";
      svgEl.style.transformBox = "fill-box";
      svgEl.style.transformOrigin = "center";
      svgEl.style.transition = "fill 0.18s ease, transform 0.18s ease-out, filter 0.2s ease";

      svgEl.style.fill = fill;
      svgEl.style.opacity = "1";

      // Selected state uses the project's signature hard offset shadow (SVG
      // filter) — never a stroke or outline. Berry-shadow when the tile
      // itself is brand-colored, Ink-shadow when it's quiet, so the shadow
      // never reads as a duplicate of the fill.
      if (isSelected) {
        svgEl.style.filter = isActive ? "url(#hard-shadow-berry)" : "url(#hard-shadow-ink)";
        svgEl.style.transform = "translate(-2px, -2px)";
      } else {
        svgEl.style.filter = "";
        svgEl.style.transform = "";
      }

      if (isFirstRender) {
        svgEl.style.animation = `svg-fade-in 0.35s ease-out ${slugIdx * 40}ms both`;
      }

      // ── Inject label + count text ─────────────────────────────────────────
      if (isActive) {
        // Active tile: BIG count + small uppercase label underneath. Color
        // picked per fill so WCAG holds across all five palette colours
        // (cream on Berry/Ink/Slate, ink on Lime/Mist).
        const labelColor = labelOnFill(fill);

        const countText = upsertText(svgEl, "count");
        countText.setAttribute("x", cx.toString());
        countText.setAttribute("y", (cy - 2).toString());
        countText.setAttribute("font-size", "26");
        countText.setAttribute("font-weight", "700");
        countText.setAttribute("fill", labelColor);
        countText.setAttribute("letter-spacing", "-0.01em");
        countText.style.fontVariantNumeric = "tabular-nums";
        countText.textContent = String(count);
        if (isFirstRender) {
          countText.style.animation = `svg-fade-in 0.35s ease-out ${slugIdx * 40 + 120}ms both`;
        }

        const labelText = upsertText(svgEl, "label");
        labelText.setAttribute("x", cx.toString());
        labelText.setAttribute("y", (cy + 22).toString());
        labelText.setAttribute("font-size", "9.5");
        labelText.setAttribute("font-weight", "600");
        labelText.setAttribute("fill", labelColor);
        labelText.setAttribute("fill-opacity", "0.85");
        labelText.setAttribute("letter-spacing", "0.08em");
        labelText.textContent = displayName.toUpperCase();
        if (isFirstRender) {
          labelText.style.animation = `svg-fade-in 0.35s ease-out ${slugIdx * 40 + 120}ms both`;
        }
      } else {
        // Inactive tile: single centred label, charcoal so it passes contrast
        // on rc-fog. Drop any lingering count text from a previous active.
        container.querySelector(`text[data-tag="count"][data-for="${slug}"]`)?.remove();

        const labelText = upsertText(svgEl, "label");
        labelText.setAttribute("x", cx.toString());
        labelText.setAttribute("y", (cy + 4).toString());
        labelText.setAttribute("font-size", "11");
        labelText.setAttribute("font-weight", "500");
        labelText.setAttribute("fill", INACTIVE_LABEL);
        labelText.setAttribute("letter-spacing", "0");
        labelText.textContent = displayName;
        if (isFirstRender) {
          labelText.style.animation = `svg-fade-in 0.35s ease-out ${slugIdx * 40 + 120}ms both`;
        }
      }

      const handleMouseMove = (e: Event) => {
        const me = e as MouseEvent;
        setTooltip({ slug, x: me.clientX, y: me.clientY });
      };
      const handleMouseLeave = () => setTooltip(null);
      const handleClick = () => {
        setPanel((prev) => (prev?.slug === slug ? null : { slug }));
        setTooltip(null);
      };
      const handleKeyDown = (e: Event) => {
        const ke = e as KeyboardEvent;
        if (ke.key === "Enter" || ke.key === " ") {
          ke.preventDefault();
          setPanel((prev) => (prev?.slug === slug ? null : { slug }));
          setTooltip(null);
        }
      };
      const handleMouseEnter = () => {
        if (!isSelected) svgEl.style.transform = "scale(1.025)";
      };
      const handleMouseOut = () => {
        if (!isSelected) svgEl.style.transform = "";
      };
      const handleFocus = () => {
        if (!isSelected) svgEl.style.filter = "url(#hard-shadow-ink)";
      };
      const handleBlur = () => {
        if (!isSelected) svgEl.style.filter = "";
      };

      addHandler(el, "mousemove", handleMouseMove as EventListener);
      addHandler(el, "mouseleave", handleMouseLeave as EventListener);
      addHandler(el, "mouseout", handleMouseOut as EventListener);
      addHandler(el, "mouseenter", handleMouseEnter as EventListener);
      addHandler(el, "click", handleClick as EventListener);
      addHandler(el, "keydown", handleKeyDown as EventListener);
      addHandler(el, "focus", handleFocus as EventListener);
      addHandler(el, "blur", handleBlur as EventListener);
    });

    return () => {
      handlers.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, panel?.slug]);

  // ── No-streams fallback ────────────────────────────────────────────────────
  if (!loading && data.length === 0) {
    return (
      <div className="bg-rc-paper border border-rc-fog rounded-2xl p-6 md:p-8">
        <div className="text-xl md:text-2xl font-bold text-rc-black mb-5">
          Stadtteil Heatmap
        </div>
        <div className="flex flex-col items-center justify-center py-12 md:py-16 text-center gap-4">
          <div className="w-11 h-11 rounded-full bg-rc-fog/60 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-rc-slate" aria-hidden="true" />
          </div>
          <div className="max-w-xs">
            <p className="text-[14px] font-semibold text-rc-black mb-2">
              Geografische Visualisierung ist noch nicht aktiv.
            </p>
            <p className="text-[13px] text-rc-slate leading-[1.6]">
              Sobald Streams im Event-Setup hinterlegt sind, erscheinen die Stadtbezirke hier mit Live-Themen.
            </p>
          </div>
          <a
            href="/facilitator"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rc-berry hover:text-rc-berry-700 transition-colors"
          >
            Streams konfigurieren →
          </a>
        </div>
      </div>
    );
  }

  const hasPanelOpen = panel !== null;

  return (
    <div className="bg-rc-paper border border-rc-fog rounded-2xl overflow-hidden">
      {/* ── Section header ────────────────────────────────────────────────── */}
      <div className="px-6 md:px-8 py-5 border-b border-rc-fog flex items-center justify-between gap-4">
        <div className="text-xl md:text-2xl font-bold text-rc-black">
          Stadtteil Heatmap
        </div>
        {data.length > 0 && (
          <ShareButton
            label="Stadtteil Heatmap teilen"
            title={eventTitle}
            cardContent={() => (
              <StadtteilSocialContent
                data={data}
                colorMap={colorMap}
                eventTitle={eventTitle}
              />
            )}
          />
        )}
      </div>

      {/* ── Main layout ───────────────────────────────────────────────────── */}
      <div
        className={`flex flex-col ${hasPanelOpen ? "lg:flex-row" : ""}`}
        ref={containerRef}
        style={{ position: "relative" }}
      >
        {/* ── Map column ──────────────────────────────────────────────────── */}
        <div
          className={`relative ${hasPanelOpen ? "lg:flex-1" : "w-full"} max-w-full overflow-x-auto`}
          style={{ minHeight: 260 }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-5 h-5 border-2 border-rc-fog border-t-rc-berry rounded-full animate-spin" />
            </div>
          ) : (
            <div
              ref={mapRef}
              className="w-full px-4 py-6 md:px-8 md:py-8"
              dangerouslySetInnerHTML={{ __html: mapSvg }}
              style={{
                // Let SVG scale to container width
                lineHeight: 0,
              }}
              aria-label="Hannover Stadtbezirke Heatmap — Klicken für Details"
            />
          )}

          {/* Tooltip */}
          {tooltip && (
            <HeatmapTooltip
              tooltip={tooltip}
              aggMap={aggMap}
              colorMap={colorMap}
              containerRef={containerRef}
            />
          )}
        </div>

        {/* ── Side panel (desktop: right col; mobile: below map) ─────────── */}
        {hasPanelOpen && (
          <div
            className="
              w-full lg:w-80 xl:w-96 flex-shrink-0
              border-t border-rc-fog lg:border-t-0 lg:border-l
              animate-fade-up lg:animate-none
            "
            style={{ minHeight: 200 }}
          >
            <SidePanel
              panel={panel}
              aggMap={aggMap}
              colorMap={colorMap}
              onClose={() => setPanel(null)}
            />
          </div>
        )}
      </div>

      {/* ── Legend ────────────────────────────────────────────────────────── */}
      {!loading && data.length > 0 && (
        <div className="px-6 md:px-8 py-4 border-t border-rc-fog flex flex-wrap gap-x-5 gap-y-2">
          {data
            .filter((d) => d.response_count > 0)
            .sort((a, b) => b.response_count - a.response_count)
            .slice(0, 5)
            .map((d) => {
              const idx = colorMap.get(d.geo_tag) ?? 0;
              return (
                <button
                  key={d.geo_tag}
                  type="button"
                  onClick={() =>
                    setPanel((prev) =>
                      prev?.slug === d.geo_tag ? null : { slug: d.geo_tag },
                    )
                  }
                  className="flex items-center gap-1.5 text-left hover:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-rc-berry focus-visible:ring-offset-2 rounded"
                  aria-label={`${getDisplayName(d.geo_tag)} anzeigen, ${d.response_count} ${d.response_count === 1 ? "Beitrag" : "Beiträge"}`}
                >
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: THEME_COLORS[idx % THEME_COLORS.length] }}
                  />
                  <span className="text-[12px] text-rc-charcoal">
                    {getDisplayName(d.geo_tag)}
                  </span>
                  <span className="text-[12px] font-semibold text-rc-black tabular-nums ml-0.5">
                    {d.response_count}
                  </span>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}
